/* ============================================================================
   Enshield public design system — PRIMITIVES (React)
   ----------------------------------------------------------------------------
   Thin, typed wrappers over the classes in system.css. They exist so a page
   composes named intent ("a standard band on the alt surface") rather than
   remembering class strings, and so section spacing stays controlled in one
   place instead of drifting per route.
   ========================================================================== */
import type { ElementType, ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { publicMotion, publicTransition, publicVariants } from '../lib/motion';
import { BorderBeamTrace } from '../components/motion';

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// Motion component identities must be module-stable. Creating them during
// render remounts descendants on every state update (a contact form kept only
// the first typed character before this was made stable).
const MotionRouterLink = motion.create(Link);

/* ---- Container ---------------------------------------------------------- */
export type ContainerWidth = 'site' | 'read' | 'wide';

export function Container({
  width = 'site',
  className,
  children,
}: {
  width?: ContainerWidth;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cx('u-container', width === 'read' && 'u-container--read', width === 'wide' && 'u-container--wide', className)}>
      {children}
    </div>
  );
}

/* ---- Band ---------------------------------------------------------------
   A page section. `space` is one of three rhythm levels and `tone` is the
   surface it sits on — the only two dimensions a section gets to choose. */
export type BandSpace = 'compact' | 'standard' | 'spacious';
export type BandTone = 'surface' | 'alt' | 'tint' | 'dark';

export function Band({
  space = 'standard',
  tone = 'surface',
  width = 'site',
  id,
  className,
  labelledBy,
  label,
  children,
  as: Tag = 'section',
  flushTop,
  flushBottom,
  bare,
  testId,
}: {
  space?: BandSpace;
  tone?: BandTone;
  width?: ContainerWidth;
  id?: string;
  className?: string;
  labelledBy?: string;
  label?: string;
  children: ReactNode;
  as?: ElementType;
  flushTop?: boolean;
  flushBottom?: boolean;
  /** Skip the inner container — for bands that manage their own layout. */
  bare?: boolean;
  /** Test hook. Named rather than spread so the prop surface stays explicit. */
  testId?: string;
}) {
  const reducedMotion = useReducedMotion();
  const MotionTag = Tag === 'div' ? motion.div : Tag === 'article' ? motion.article : motion.section;
  return (
    <MotionTag
      id={id}
      data-testid={testId}
      data-motion="section"
      aria-labelledby={labelledBy}
      aria-label={label}
      className={cx(
        'u-band',
        space === 'compact' && 'u-band--compact',
        space === 'spacious' && 'u-band--spacious',
        `u-band--${tone}`,
        flushTop && 'u-band--flush-top',
        flushBottom && 'u-band--flush-bottom',
        className,
      )}
      initial={reducedMotion ? false : { opacity: 0, y: publicMotion.distance.interface }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.08 }}
      transition={publicTransition(!!reducedMotion, publicMotion.duration.scene)}
    >
      {bare ? children : <Container width={width}>{children}</Container>}
    </MotionTag>
  );
}

/* ---- Eyebrow ------------------------------------------------------------ */
export function Eyebrow({ pill, children, className }: { pill?: boolean; children: ReactNode; className?: string }) {
  return <p className={cx('u-eyebrow', pill && 'u-eyebrow--pill', className)}>{children}</p>;
}

/* ---- Section header -----------------------------------------------------
   Eyebrow + heading + optional lead, spaced once so every section on every
   route shares the same internal rhythm. */
export function SectionHead({
  eyebrow,
  title,
  titleId,
  lead,
  align = 'start',
  level = 2,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  titleId?: string;
  lead?: ReactNode;
  align?: 'start' | 'center';
  level?: 1 | 2;
  className?: string;
}) {
  const Heading = (level === 1 ? 'h1' : 'h2') as ElementType;
  return (
    <div className={cx('u-head', align === 'center' && 'u-head--center', className)}>
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <Heading id={titleId} className={level === 1 ? 'u-h1' : 'u-h2'}>
        {title}
      </Heading>
      {lead ? <p className="u-lead">{lead}</p> : null}
    </div>
  );
}

/* ---- Button -------------------------------------------------------------
   `to` renders a router Link, `href` an anchor, neither a <button>. Keeping
   this one component means the control geometry cannot drift between a
   navigation CTA and a form action. */
export type BtnTone = 'primary' | 'secondary' | 'ghost';

type BtnCommon = { tone?: BtnTone; className?: string; children: ReactNode };

export function Btn({ tone = 'primary', className, children, ...rest }: BtnCommon & (
  | { to: string; href?: never; type?: never; onClick?: never }
  | { href: string; to?: never; type?: never; onClick?: never }
  | { to?: never; href?: never; type?: 'button' | 'submit'; onClick?: () => void }
)) {
  const reducedMotion = useReducedMotion();
  const cls = cx('u-btn', `u-btn--${tone}`, className);
  const interaction = {
    whileHover: reducedMotion ? undefined : { y: -2, scale: 1.015 },
    whileTap: reducedMotion ? undefined : { y: 1, scale: 0.985 },
    transition: publicMotion.spring.interface,
  };
  if ('to' in rest && rest.to) return <MotionRouterLink data-motion="motion-button" className={cls} to={rest.to} {...interaction}>{children}</MotionRouterLink>;
  if ('href' in rest && rest.href) return <motion.a data-motion="motion-button" className={cls} href={rest.href} {...interaction}>{children}</motion.a>;
  return (
    <motion.button data-motion="motion-button" className={cls} type={('type' in rest && rest.type) || 'button'} onClick={'onClick' in rest ? rest.onClick : undefined} {...interaction}>
      {children}
    </motion.button>
  );
}

/* ---- Action link -------------------------------------------------------- */
export function ActionLink({ to, href, children, className }: { to?: string; href?: string; children: ReactNode; className?: string }) {
  const cls = cx('u-link', className);
  const body = <>{children}<span aria-hidden="true">→</span></>;
  if (to) return <Link className={cls} to={to}>{body}</Link>;
  return <a className={cls} href={href}>{body}</a>;
}

/* ---- Grid --------------------------------------------------------------- */
export function Grid({
  cols = 3,
  tight,
  className,
  children,
  as: Tag = 'div',
  label,
}: {
  cols?: 2 | 3 | 4;
  tight?: boolean;
  className?: string;
  children: ReactNode;
  as?: ElementType;
  label?: string;
}) {
  const reducedMotion = useReducedMotion();
  const MotionTag = Tag === 'section' ? motion.section : Tag === 'ul' ? motion.ul : Tag === 'ol' ? motion.ol : motion.div;
  return (
    <MotionTag
      aria-label={label}
      data-motion="stagger-grid"
      className={cx('u-grid', `u-grid--${cols}`, tight && 'u-grid--tight', className)}
      initial={reducedMotion ? false : 'hidden'}
      whileInView="show"
      viewport={{ once: true, amount: 0.12 }}
      variants={{ hidden: {}, show: { transition: { staggerChildren: reducedMotion ? 0 : publicMotion.stagger.standard } } }}
    >
      {children}
    </MotionTag>
  );
}

/* ---- Split -------------------------------------------------------------- */
export function Split({
  weight = 'even',
  reverse,
  className,
  children,
}: {
  weight?: 'even' | 'copy' | 'visual';
  reverse?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cx(
        'u-split',
        weight === 'copy' && 'u-split--copy-wide',
        weight === 'visual' && 'u-split--visual-wide',
        reverse && 'u-split--reverse',
        className,
      )}
    >
      {children}
    </div>
  );
}

/* ---- Card --------------------------------------------------------------- */
export function Card({
  to,
  quiet,
  className,
  children,
  as: Tag = 'article',
}: {
  to?: string;
  quiet?: boolean;
  className?: string;
  children: ReactNode;
  as?: ElementType;
}) {
  const reducedMotion = useReducedMotion();
  const cls = cx('u-card', quiet && 'u-card--quiet', to && 'u-card--interactive', className);
  const variants = {
    hidden: reducedMotion ? {} : publicVariants.card.hidden,
    show: { ...publicVariants.card.show, transition: publicTransition(!!reducedMotion) },
  };
  if (to) {
    return <MotionRouterLink data-motion="depth-card" className={cls} to={to} variants={variants} whileHover={reducedMotion ? undefined : { y: -3, scale: 1.006 }} transition={publicMotion.spring.interface}><BorderBeamTrace duration={publicMotion.duration.ambient} />{children}</MotionRouterLink>;
  }
  const MotionTag = Tag === 'div' ? motion.div : Tag === 'section' ? motion.section : motion.article;
  return <MotionTag data-motion="depth-card" className={cls} variants={variants} whileHover={reducedMotion ? undefined : { y: -3, scale: 1.006 }} transition={publicMotion.spring.interface}><BorderBeamTrace duration={publicMotion.duration.ambient} />{children}</MotionTag>;
}

/* ---- Icon plate --------------------------------------------------------- */
export function Plate({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx('u-plate', className)} aria-hidden="true">{children}</span>;
}
